<template>
    <modal :modal-id="modalId"
           modal-size="large"
           :title="selectedUrl ? $t('edit_expense') : $t('add_expense')"
           :preloader="preloader"
           @submit="submitData"
           @closeModal="closeModal">
        <template slot="body">
            <app-overlay-loader v-if="preloader"/>
            <form v-else class="mb-0"
                  :class="{'loading-opacity': preloader}"
                  ref="form"
                  :data-url="selectedUrl ? selectedUrl : 'expenses'">

                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <label for="client_id">
                        {{ $t('Supplier') }} <sup class="text-size-20 top-1">*</sup>
                      </label>
                      <div class="d-flex align-items-center w-100">
                        <app-input
                            id="client_id"
                            class="margin-right-8"
                            type="search-and-select"
                            :placeholder="$t('choose_a_client')"
                            :options="clientOptions"
                            v-model="formData.client_id"
                            :error-message="$errorMessage(errors, 'client_id')"
                        />
                        <button class="btn btn-primary ml-2" type="button" @click="openClientModal">
                          <app-icon name="plus" :size="16"/>
                        </button>
                      </div>
                    </div>
                  </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label for="name">
                                {{ $t('name') }} <sup class="text-size-20 top-1">*</sup>
                            </label>
                            <app-input
                                id="name"
                                type="text"
                                :placeholder="$t('enter_name')"
                                v-model="formData.name"
                                :error-message="$errorMessage(errors, 'name')"
                            />
                        </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="invoice_number">
                          {{ $t('part_number') }} <sup class="text-size-20 top-1"></sup>
                        </label>
                        <app-input
                            id="invoice_number"
                            type="text"
                            v-model="formData.invoice_number"
                            :error-message="$errorMessage(errors, 'invoice_number')"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label for="amount">
                                {{ $t('amount') }}<sup class="text-size-20 top-1">*</sup>
                            </label>
                            <app-input
                                id="amount"
                                type="number"
                                :placeholder="$t('enter_amount')"
                                v-model="formData.amount"
                                :error-message="$errorMessage(errors, 'amount')"
                                @input="amountToTotal(formData)"
                                :readOnly="true"
                            />
                        </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="reference">
                          {{ $t('reference_invoice_number') }}<sup class="text-size-20 top-1">*</sup>
                        </label>
                        <app-input
                            id="reference"
                            type="text"
                            :placeholder="$t('enter_reference')"
                            v-model="formData.reference"
                            :error-message="$errorMessage(errors, 'reference')"
                        />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label for="amount">
                          {{ $t('tax') }}<sup class="text-size-20 top-1">*</sup>
                        </label>
                        <app-input
                            v-model="formData.tax_id"
                            :list="taxList"
                            :placeholder="taxList && taxList.length > 1 ? $t('choose_tax') : $t('n_a_tax')"
                            list-value-field="name"
                            type="select"
                            @input="changeTax()"
                        />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label for="tax_value">
                          {{ $t('tax_value') }}<sup class="text-size-20 top-1">*</sup>
                        </label>
                        <app-input
                            id="tax_value"
                            type="number"
                            :placeholder="$t('enter_amount')"
                            v-model="formData.tax_value"
                            :error-message="$errorMessage(errors, 'tax_value')"
                            :readOnly="true"
                        />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label for="tax_value">
                          {{ $t('total') }}<sup class="text-size-20 top-1">*</sup>
                        </label>
                        <app-input
                            id="tax_total"
                            type="number"
                            :placeholder="$t('enter_amount')"
                            v-model="formData.tax_total"
                            :error-message="$errorMessage(errors, 'tax_total')"
                            :readonly="true"
                            @input="totalToToAmount(formData)"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label for="date">
                                {{ $t('date') }}<sup class="text-size-20 top-1">*</sup>
                            </label>
                            <app-input
                                id="date"
                                type="date"
                                :placeholder="$t('enter_date')"
                                v-model="formData.date"
                                :max-date="new Date()"
                                :error-message="$errorMessage(errors, 'date')"
                            />
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label for="expense_purpose">
                                {{ $t('expense_purpose') }} <sup class="text-size-20 top-1">*</sup>
                            </label>
                            <div class="d-flex align-items-center">
                                <div class="flex-grow-1">
                                    <app-input
                                        id="expense_purpose"
                                        type="search-select"
                                        :placeholder="$t('select_expense_purpose')"
                                        :list="expensePurposeList"
                                        list-value-field="name"
                                        v-model="formData.purpose_id"
                                        :error-message="$errorMessage(errors, 'purpose_id')"
                                        @input="changeExpensePurpose(formData.purpose_id)"
                                    />
                                </div>
                                <button type="button"
                                        data-toggle="tooltip"
                                        :title="$t('add_purpose')"
                                        class="btn btn-primary ml-1"
                                        @click="openPurposeAddEditModal">
                                    <app-icon name="plus"/>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-12 assets-container">
                        <div class="form-group">
                            <label for="asset_id">
                                {{ $t('expense_asset') }} <sup class="text-size-20 top-1"></sup>
                            </label>
                          <app-input
                              id="asset_id"
                              v-model="formData.asset_id"
                              :error-message="$errorMessage(errors, 'asset_id')"
                              :list="assets"
                              :placeholder="$t('choose_a_status')"
                              list-value-field="type_with_code"
                              type="search-select"
                          />
                        </div>
                    </div>

                    <div class="col-md-12 mb-3">
                        <div class="form-group">
                            <label for="note">{{ $t('note') }}</label>
                            <app-input
                                id="note"
                                type="textarea"
                                :text-area-rows="3"
                                :placeholder="$t('enter_note')"
                                v-model="formData.note"
                            />
                        </div>
                    </div>
                    <div class="col-md-12 mb-3">
                        <div class="form-group">
                            <app-input v-model="files" type="dropzone"/>
	                        <small>
		                        {{$t('allow_file_type')}}
	                        </small>
	                        <br>
                            <small v-if="maxFileWarning" class="text-danger">
                                {{$t('multiple_file_is_not_allowed_please_select_one_file')}}
                            </small>
	                        
                            <small v-if="errors.attachment" class="text-danger">
                                {{ errors.attachment[0] }}
                            </small>
                        </div>
                    </div>

                </div>
            </form>
          <client-add-edit-modal
              v-if="isClientModalActive"
              :table-id="'client-table'"
              :client-number="clientNumber"
              @closeModal="closeClientModal"
          />
        </template>
    </modal>
</template>

<script>
import {SubmitFormMixins} from "../../../Mixins/billar/SubmitFormMixins";
import {axiosGet, urlGenerator} from "../../../Helpers/AxiosHelper";
import {formDataAssigner} from "../../../Helpers/Helpers";
import {formatDateForServer} from "../../../Helpers/DateTimeHelper";
import {vendorFilter} from "../../../Mixins/FilterMixin";

export default {
    name: "ExpenseCreateEditModal",
    mixins: [SubmitFormMixins, vendorFilter],
    props: {
      taxList: {},
      assets: {},
      clientNumber: '',
    },
    data() {
        return {
            formData: {},
            isClientModalActive: false,
            expensePurposeList: [],
            files: [],
            maxFileWarning: false,
            modalId: 'expense-create-edit-modal',
            clientOptions: {
              url: urlGenerator('supplier-users'),
              per_page: 10,
              vendor: 1,
              loader: "app-pre-loader",
              modifire: (value) => ({id: value.id, value: value.full_name}),
            },
        }
    },
    methods: {
        submitData() {
            if (this.files.length > 1) {
                this.maxFileWarning = true;
                return;
            }
            this.maxFileWarning = false;
            this.formData.date = formatDateForServer(this.formData?.date);
            let formData = formDataAssigner(new FormData(), this.formData);
            if (this.files.length) {
                formData.append("attachment", this.files[0]);
            }
            if (this.selectedUrl) {
                formData.append('_method', 'patch');
                this.submitFromFixin('post', this.selectedUrl, formData)
            } else {
                this.submitFromFixin('post', 'expenses', formData)
            }
        },
        afterSuccessFromGetEditData(response) {
            this.formData = response.data;
			this.preloader = false;
        },
        getExpensePurposeList() {
            axiosGet('purpose-list?type=expense').then(({data}) => {
                this.expensePurposeList = data;
            })
        },
        openPurposeAddEditModal(){
            this.$emit('openPurposeAddEditModal');
        },
        changeTax() {
          // if (this.formData.tax_id) {
          //   let taxObj = this.taxList.find(item => item.id === Number(this.formData.tax_id));
          //   var tax = parseFloat(this.formData.amount * (taxObj.value / 100));
          //   this.formData.tax_value = tax;
          //   this.formData.tax_total = parseFloat(this.formData.amount) + tax;
          // } else {
          //   this.formData.tax_value = 0;
          //   this.formData.tax_total = (this.formData.amount);
          // }
          this.totalToToAmount(this.formData)
        },
        totalToToAmount(formData){
          if (formData.tax_id) {
            let taxObj = this.taxList.find(item => item.id === Number(formData.tax_id));
            let beforeTax = parseFloat(formData.tax_total/(1 + (taxObj.value / 100)));
            this.formData.tax_value = parseFloat(beforeTax * (taxObj.value / 100)).toFixed(settings.number_of_decimal);
            formData.amount = parseFloat(formData.tax_total/(1 + (taxObj.value / 100))).toFixed(settings.number_of_decimal);
          }else{
            formData.amount = formData.tax_total;
          }
        },
        amountToTotal(formData){
          if (formData.tax_id) {
            let taxObj = this.taxList.find(item => item.id === Number(formData.tax_id));
            var tax = parseFloat(formData.amount * (taxObj.value / 100));
            formData.tax_total =  parseFloat(formData.amount) + tax;
          }else{
            formData.tax_total = formData.amount;
          }
        },
        openClientModal() {
          this.isClientModalActive = true;
        },
        closeClientModal() {
          this.isClientModalActive = false;
        },
        changeExpensePurpose(id){
          if(id === 1){
            $('.assets-container').show();
          }else{
            $('.assets-container').hide();
            this.formData.asset_id = null;
          }
        },
        changeExpensePurposeOnMount(id){
          if(id === 1){
            $('.assets-container').show();
          }else{
            $('.assets-container').hide();
          }
        },
    },
  mounted() {
        this.getExpensePurposeList();
        this.$hub.$on('new-purpose-added', () => {
            this.getExpensePurposeList();
        });
        setTimeout(() => this.changeExpensePurposeOnMount(this.formData.purpose_id), 500);
    },
  // updated(){
  //   document.getElementById('formData_tax_id').dispatchEvent(new Event('input'));
  // },
}
</script>

<style scoped>

</style>
